/* Imports */
import { isAffirmLocale } from '../helpers/affirm/config.js';
import { getShippingTimeTranslation } from '../helpers/getShippingTimeTranslation.js';

export default {
  state: {
    partNumber: '',
    productInfoId: '',
    productLegalText: '',
    productMarketingSummary: '',
    productName: '',
    simpleDisplayName: '',
    globalProductName: '',
    productVariation: '',
    subheadDisplayName: '',
    shippingDisclaimer: '',
    showPartNumber: false,
    showPrice: false,
    showProductVariationName: false,
    showCompatibleDevicesTab: false,
    productHeaderDetails: '',
    productAvailability: '',
    sellable: false,
    // EMS TODO - Remove after we remove the ITFE_PRODUCT_PAGE_ENABLE_REUSABLE_EMS_UI feature flag
    productEmsList: [],
    // EMS TODO - Remove after we remove the ITFE_PRODUCT_PAGE_ENABLE_REUSABLE_EMS_UI feature flag
    filterDropdownList: [],
    pvProductEcommerceOpenText: '',
    productKickerData: {},
    productDisplayCustomizeButton: false,
    productCustomizeButtonUrl: '',
    pvName: '',
    isAffirmScriptLoaded: false,
    truemedEligible: false,
  },
  getters: {
    getPartNumber(state) {
      return state.partNumber;
    },
    getPvProductEcommerceOpenText(state) {
      return state.pvProductEcommerceOpenText;
    },
    getProductInfoId(state) {
      return state.productId;
    },
    getProductLegalText(state) {
      return state.productLegalText;
    },
    getShowProductLegalText(state, getters) {
      return !!getters.getProductLegalText.length;
    },
    getProductMarketingSummary(state) {
      return state.productMarketingSummary;
    },
    getShowProductMarketingSummary(state, getters) {
      return !!getters.getProductMarketingSummary.length;
    },
    getProductName(state) {
      return state.productName;
    },
    getSimpleDisplayName(state) {
      return state.simpleDisplayName;
    },
    getGlobalProductName(state) {
      return state.globalProductName;
    },
    getProductVariation(state) {
      return state.productVariation;
    },
    getSubheadDisplayName(state) {
      return state.subheadDisplayName;
    },
    getShippingDisclaimer(state) {
      return state.shippingDisclaimer;
    },
    getShowShippingDisclaimer(state, getters) {
      return !!getters.getShippingDisclaimer.length;
    },
    getProductHeaderDetails(state) {
      return state.productHeaderDetails;
    },
    getShowProductHeaderDetails(state, getters) {
      return !!getters.getProductHeaderDetails.length;
    },
    getShowProductName(state, getters) {
      return !!getters.getProductName;
    },
    getShowPartNumber(state) {
      return state.showPartNumber;
    },
    getShowPrice(state) {
      return state.showPrice;
    },
    getShowProductVariationName(state) {
      return state.showProductVariationName;
    },
    getShowCompatibleDevicesTab(state) {
      return state.showCompatibleDevicesTab;
    },
    getProductAvailability(state) {
      return state.productAvailability;
    },
    getShowProductAvailability(state, getters) {
      return !!getters.getProductAvailability.length;
    },
    getSellable(state) {
      return state.sellable;
    },
    // EMS TODO - Remove after we remove the ITFE_PRODUCT_PAGE_ENABLE_REUSABLE_EMS_UI feature flag
    getProductEmsList(state) {
      return state.productEmsList;
    },
    // EMS TODO - Remove after we remove the ITFE_PRODUCT_PAGE_ENABLE_REUSABLE_EMS_UI feature flag
    getFilterDropdownList(state) {
      return state.filterDropdownList;
    },
    // EMS TODO - Remove after we remove the ITFE_PRODUCT_PAGE_ENABLE_REUSABLE_EMS_UI feature flag
    getFilterDropdownSelected(state) {
      // Return the item that is selected or the first one in the list if nothing is selected
      return state.filterDropdownList.find((option) => option.selected)
             || state.filterDropdownList[0]
             || {};
    },
    getProductDisplayCustomizeButton: (state) => state.productDisplayCustomizeButton,
    getProductCustomizeButtonUrl: (state) => state.productCustomizeButtonUrl,
    getPvName: (state) => state.pvName,
    getIsAffirmScriptLoaded: (state) => state.isAffirmScriptLoaded,
    /**
     * Determines whether the Affirm widget should be displayed.
     * The widget is shown only if:
     * - The 'ATC' button is visible, and
     * - The user's locale is supported by Affirm according to the Contentful configuration.
     * @param {object} state - The Vuex state object (unused in this getter).
     * @param {object} getters - The Vuex getters object.
     * @param {boolean} getters.getShowAddToCartBtn - Whether the 'ATC' button is visible.
     * @param {string} getters.getLocale - The current locale of the user.
     * @returns {boolean} `true` if the Affirm widget should be displayed, otherwise `false`.
     */
    getShowAffirmWidget: (state, getters) => getters.getShowAddToCartBtn && isAffirmLocale(getters.getLocale),
    getLoadKlarnaApi: (state, getters) => !getters.getShowAPACWidget && !isAffirmLocale(getters.getLocale),
    getShowKlarnaWidget: (state, getters) => {
    /**
     * Determines whether the Klarna widget should be displayed.
     * The widget is shown only if:
     * - Valid widget metadata is available, and
     * - The user's locale isn't one of the Affirm-supported locales from Contentful.
     * - The user's locale isn't configured to show the APAC widget.
     * @param {object} state - The Vuex state object (unused in this getter).
     * @param {object} getters - The Vuex getters object.
     * @param {object} getters.getWidgetMetadata - The widget metadata object.
     * @param {string} getters.getLoadKlarnaApi - Whether the Klarna API should be loaded based on the user's locale.
     * @returns {boolean} `true` if the Klarna widget should be displayed, otherwise `false`.
     */
      const hasWidgetMetadata = Object.keys(getters.getWidgetMetadata)?.length > 0;
      return hasWidgetMetadata && getters.getLoadKlarnaApi;
    },
    getShowAPACWidget: () => window?.showAPACWidget,
    getAPACWidgetScriptUrl: () => window?.apacWidgetScriptUrl,
    getTruemedEligible: (state) => state.truemedEligible,
  },
  mutations: {
    setPartNumber(state, payload) {
      state.partNumber = payload;
    },
    setProductInfoId(state, payload) {
      state.productId = payload;
    },
    setProductMarketingSummary(state, payload) {
      state.productMarketingSummary = payload || '';
    },
    setProductName(state, payload) {
      state.productName = payload;
    },
    setSimpleDisplayName(state, payload) {
      state.simpleDisplayName = payload;
    },
    setGlobalProductName(state, payload) {
      state.globalProductName = payload;
    },
    setProductVariation(state, payload) {
      state.productVariation = payload;
    },
    setSubheadDisplayName(state, payload) {
      state.subheadDisplayName = payload;
    },
    setShippingDisclaimer(state, payload) {
      state.shippingDisclaimer = payload || '';
    },
    setProductHeaderDetails(state, payload) {
      state.productHeaderDetails = payload || '';
    },
    setProductLegalText(state, payload) {
      state.productLegalText = payload || '';
    },
    setShowPartNumber(state, payload) {
      state.showPartNumber = payload;
    },
    setShowPrice(state, payload) {
      state.showPrice = payload;
    },
    setShowProductVariationName(state, payload) {
      state.showProductVariationName = payload;
    },
    setShowCompatibleDevicesTab(state, payload) {
      state.showCompatibleDevicesTab = !!payload;
    },
    setProductAvailability(state, payload) {
      state.productAvailability = payload || '';
    },
    setSellable(state, payload) {
      state.sellable = payload;
    },
    // EMS TODO - Remove after we remove the ITFE_PRODUCT_PAGE_ENABLE_REUSABLE_EMS_UI feature flag
    setProductEmsList(state, payload) {
      state.productEmsList = payload;
    },
    // EMS TODO - Remove after we remove the ITFE_PRODUCT_PAGE_ENABLE_REUSABLE_EMS_UI feature flag
    setFilterDropdownList(state, payload) {
      state.filterDropdownList = payload;
    },
    // EMS TODO - Remove after we remove the ITFE_PRODUCT_PAGE_ENABLE_REUSABLE_EMS_UI feature flag
    setFilterDropdownSelected(state, payload) {
      state.filterDropdownList = state.filterDropdownList.map((option) => ({
        ...option,
        selected: payload === option.value,
      }));
    },
    setPvProductEcommerceOpenText(state, payload) {
      state.pvProductEcommerceOpenText = payload;
    },
    setProductDisplayCustomizeButton(state, payload) {
      state.productDisplayCustomizeButton = payload;
    },
    setProductCustomizeButtonUrl(state, payload) {
      state.productCustomizeButtonUrl = payload;
    },
    setPvName(state, payload) {
      state.pvName = payload;
    },
    setIsAffirmScriptLoaded(state, payload) {
      state.isAffirmScriptLoaded = payload;
    },
    setTruemedEligible(state, payload) {
      state.truemedEligible = payload;
    },
  },
  actions: {
    async setProductInfo({ commit, dispatch }, payload) {
      const {
        partNumber,
        productId,
        productLegalText,
        promoBanner,
        productMarketingSummary,
        productName,
        simpleDisplayName,
        globalProductName,
        productVariation,
        subheadDisplayName,
        showPartNumber,
        showPrice,
        showProductVariationName,
        showCompatibleDevicesTab,
        productHeaderDetails,
        productAvailability,
        productDisplayYWYWButton,
        productYWYWButton,
        sellable,
        seo,
        pvProductEcommerceOpenText,
        pvName,
        truemedEligible,
        shippingDisclaimer,
      } = payload;

      commit('setPartNumber', partNumber);
      commit('setProductId', productId);
      commit('setProductLegalText', productLegalText);
      commit('setProductMarketingSummary', productMarketingSummary);
      commit('setProductName', productName);
      commit('setSimpleDisplayName', simpleDisplayName);
      commit('setGlobalProductName', globalProductName);
      commit('setProductVariation', productVariation);
      commit('setSubheadDisplayName', subheadDisplayName);
      commit('setShowPartNumber', showPartNumber);
      commit('setShowPrice', showPrice);
      commit('setShowProductVariationName', showProductVariationName);
      commit('setShowCompatibleDevicesTab', showCompatibleDevicesTab);
      commit('setProductHeaderDetails', productHeaderDetails);
      commit('setProductAvailability', productAvailability);
      commit('setProductDisplayCustomizeButton', productDisplayYWYWButton);
      commit('setProductCustomizeButtonUrl', productYWYWButton);
      commit('setSellable', sellable);

      if (window?.envSettings?.ITFE_PRODUCT_PAGE_ENABLE_LEAD_TIME_API) await dispatch('fetchShippingDisclaimer', partNumber);
      else commit('setShippingDisclaimer', shippingDisclaimer);

      dispatch('setPromoBanner', promoBanner);
      dispatch('setSeoTitle', seo.title);
      dispatch('setPvProductEcommerceOpenText', pvProductEcommerceOpenText);
      commit('setPvName', pvName);
      commit('setTruemedEligible', truemedEligible);
    },
    // EMS TODO - Remove after we remove the ITFE_PRODUCT_PAGE_ENABLE_REUSABLE_EMS_UI feature flag
    setProductOptionsList({ commit }, payload) {
      const {
        productEmsList,
        filterDropdownList,
      } = payload;

      /**
       * Remove empty EMS items and EMS items that have less than 2 options
       * No point in showing EMS items in the UI that only have one option to choose from
       */
      const productEmsListCleaned = productEmsList.filter((ems) => {
        if (!Object.entries(ems).length) return false;
        if (ems.options && ems.options.length === 1) return false;
        return true;
      });

      commit('setProductEmsList', productEmsListCleaned);
      commit('setFilterDropdownList', filterDropdownList);
    },
    // EMS TODO - Remove after we remove the ITFE_PRODUCT_PAGE_ENABLE_REUSABLE_EMS_UI feature flag
    setFilterDropdownSelected({ commit }, payload) {
      commit('setFilterDropdownSelected', payload);
    },
    setShowCompatibleDevicesTab({ commit }, payload) {
      commit('setShowCompatibleDevicesTab', payload);
    },
    async fetchShippingDisclaimer({ commit, getters }, partNumber) {
      const { getBuyGarminEndpoint, getCountryCode, getShippingDisclaimerTranslations } = getters;
      try {
        const url = `${getBuyGarminEndpoint}/product-page-services/countries/${getCountryCode}/partNumber/${partNumber}/processing-time`;
        const response = await fetch(url);
        const processingTimeData = await response.json();

        if (processingTimeData.processingTime) commit('setShippingDisclaimer', getShippingTimeTranslation(processingTimeData?.processingTime, getShippingDisclaimerTranslations));
      } catch (error) {
        console.error('Error calling buy garmin service ', error);
      }
    },
    setSeoTitle(context, title) {
      // use native dom parser to parse special characters in title string
      const parser = new DOMParser();
      const dom = parser.parseFromString(title, 'text/html');

      // Prevent setting the title to null if we don;t have a seo title
      if (title && dom.body.textContent) {
        document.title = dom.body.textContent;
      }
    },
    setPvProductEcommerceOpenText({ commit }, payload) {
      commit('setPvProductEcommerceOpenText', payload);
    },
    async callKlarnaApi({ dispatch, getters }) {
      const {
        getBuyGarminEndpoint,
        getLocale,
        getPriceData,
        getSellable,
        getShowPrice,
      } = getters;
      const currencyCode = window?.utag_data?.currency_code;

      // If the product is not sellable we don't want Klarna to display
      if (!getSellable || !getShowPrice) {
        dispatch('setWidgetMetadata', {});
        return;
      }

      let price;
      if (getPriceData?.salePrice?.price) {
        price = getPriceData?.salePrice?.price;
      } else {
        price = getPriceData?.listPrice?.price;
      }

      const url = `${getBuyGarminEndpoint}/marketing-services/widgets/klarna?price=${price}&currency=${currencyCode}&locale=${getLocale}`;

      try {
        const response = await fetch(url, {
          method: 'get',
        });

        const klarnaData = await response.json();
        if (klarnaData) {
          dispatch('setWidgetMetadata', klarnaData);
        } else {
          dispatch('setWidgetMetadata', {});
        }
      } catch {
        dispatch('setWidgetMetadata', {});

        console.error('Error calling klarna marketing-services');
      }
    },
    setIsAffirmScriptLoaded({ commit }, payload) {
      commit('setIsAffirmScriptLoaded', payload);
    },
  },
};
